import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/lib/auth';
import { STRIPE_PRODUCTS, type StripePlan } from '@/lib/stripe-config';
import {
  createCheckoutSession,
  getCurrentSubscription,
  isActiveSubscription,
  cancelSubscriptionAtPeriodEnd,
  reactivateSubscription,
  formatPeriodEnd,
} from '@/lib/stripe';
import { apiFetch } from '@/lib/api';
import { getPlanFromPriceId, getPriceId } from '@/lib/stripe-config';
import { loadSubscriptionPrices, useSubscriptionPrices } from '@/lib/subscription-prices';
import { PricingCard } from '@/components/subscription/pricing-card';
import { FeaturesComparison } from '@/components/subscription/features-comparison';
import { FAQSection } from '@/components/subscription/faq-section';
import { SavingsCalculator } from '@/components/subscription/savings-calculator';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import {
  trackSubscriptionPageView,
  trackSubscriptionToggle,
  trackPlanSelected,
  trackCheckoutStarted,
} from '@/lib/analytics';

// VOY-607: the subscription fields the downgrade flow decides on.
interface SubscriptionSnapshot {
  plan: StripePlan | null;
  subscriptionId: string | null;
  cancelAtPeriodEnd: boolean;
  periodEndLabel: string | null;
}

const EMPTY_SNAPSHOT: SubscriptionSnapshot = {
  plan: null,
  subscriptionId: null,
  cancelAtPeriodEnd: false,
  periodEndLabel: null,
};

const PRICES_UNAVAILABLE = 'Plans are unavailable right now. Please try again later.';

export default function SubscriptionPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<StripePlan | null>(null);
  const [currentSubscriptionId, setCurrentSubscriptionId] = useState<string | null>(null);
  const [currentSubscriptionIsAnnual, setCurrentSubscriptionIsAnnual] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isAnnual, setIsAnnual] = useState(false);
  // VOY-607: downgrade to Free = cancel at period end. `cancelAtPeriodEnd` marks a pending
  // downgrade; `periodEndLabel` is the formatted date the paid plan ends.
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [periodEndLabel, setPeriodEndLabel] = useState<string | null>(null);
  const [showDowngradeDialog, setShowDowngradeDialog] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // VOY-607: the Free CTA sits far below the status messages; after a downgrade action,
  // scroll them into view so the result isn't rendered off-screen.
  const statusRef = useRef<HTMLDivElement>(null);
  const scrollToStatusRef = useRef(false);
  // VOY-531: the user id we last loaded the subscription for, so the load effect runs
  // once per distinct user instead of on every `user` reference change.
  const loadedForUserRef = useRef<string | null>(null);
  // VOY-601: Stripe price ids come from subscription_prices. While they load, or if the load
  // failed, no plan can be bought (the buttons have no price id).
  const { status: pricesStatus, prices } = useSubscriptionPrices();

  useEffect(() => {
    if (!scrollToStatusRef.current) return;
    scrollToStatusRef.current = false;
    statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [error, notice, cancelAtPeriodEnd]);

  // Track page view only once on mount
  useEffect(() => {
    trackSubscriptionPageView();
  }, []); // Empty dependency array - only run on mount

  // Load subscription data when user is available.
  // VOY-531: guard by user id. `user` from AuthContext changes reference several times as
  // auth resolves, which re-ran this effect ~3× per visit (redundant network calls + console
  // noise). Load once per distinct user id; reset on sign-out so a later sign-in reloads.
  useEffect(() => {
    if (!user) {
      loadedForUserRef.current = null;
      return;
    }
    if (loadedForUserRef.current === user.id) return;
    loadedForUserRef.current = user.id;
    loadCurrentSubscription();
  }, [user]);

  // Returns what it loaded (VOY-607) so a caller can act on it before the state updates land.
  async function loadCurrentSubscription(): Promise<SubscriptionSnapshot> {
    const snapshot: SubscriptionSnapshot = { ...EMPTY_SNAPSHOT };
    try {
      const subscription = await getCurrentSubscription();

      // VOY-525: only treat the subscription as the user's current plan when it is
      // actually active. A `canceled`/`not_started` row still carries a `price_id`,
      // and keying on price_id alone showed a canceled $5 sub as the current plan
      // while the profile (which checks status) correctly showed Free.
      // VOY-554: past_due is not PAID (no member pricing anywhere), but the user still holds this
      // Stripe subscription, so the page keeps showing it as their plan (not manageable here, see
      // below) instead of offering Free/new checkouts that would open a second subscription.
      const holdsPlan = isActiveSubscription(subscription) || subscription?.status === 'past_due'
      if (subscription?.price_id && holdsPlan) {
        // Await the rows here: this runs on mount, before the hook above has them. A failed load
        // throws into the catch below.
        const planInfo = getPlanFromPriceId(subscription.price_id, await loadSubscriptionPrices());
        if (planInfo) {
          snapshot.plan = planInfo.plan;
          setCurrentPlan(planInfo.plan);
          // Store whether current subscription is annual for comparison
          setCurrentSubscriptionIsAnnual(planInfo.isAnnual);
          // Sync the toggle to match the current subscription's billing interval
          setIsAnnual(planInfo.isAnnual);
        } else {
          setCurrentPlan(null);
          setCurrentSubscriptionIsAnnual(false);
          setIsAnnual(false);
        }
        // Store subscription ID if subscription is paid (VOY-525). A `past_due` subscription
        // shows its plan (above) but does NOT get the manage/cancel button here; the
        // dunning/recovery flow owns that case. VOY-554: past_due is not a paying member.
        if (subscription.subscription_id && (subscription.status === 'active' || subscription.status === 'trialing')) {
          snapshot.subscriptionId = subscription.subscription_id;
        }
        snapshot.cancelAtPeriodEnd = !!subscription.cancel_at_period_end;
        snapshot.periodEndLabel = formatPeriodEnd(subscription.current_period_end);
        setCurrentSubscriptionId(snapshot.subscriptionId);
        setCancelAtPeriodEnd(snapshot.cancelAtPeriodEnd);
        setPeriodEndLabel(snapshot.periodEndLabel);
      } else {
        setCurrentPlan(null);
        setCurrentSubscriptionId(null);
        setCancelAtPeriodEnd(false);
        setPeriodEndLabel(null);
        setCurrentSubscriptionIsAnnual(false);
        setIsAnnual(false);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
      // Reset all subscription state on error
      setCurrentPlan(null);
      setCurrentSubscriptionId(null);
      setCancelAtPeriodEnd(false);
      setPeriodEndLabel(null);
      setCurrentSubscriptionIsAnnual(false);
      setIsAnnual(false);
      return { ...EMPTY_SNAPSHOT };
    }
    return snapshot;
  }

  /**
   * VOY-607: Free has no Stripe price, so it can't go through handleSubscribe. Selecting it
   * while on a paid plan asks for confirmation, then cancels the subscription at period end.
   */
  const handleSelectFree = async () => {
    if (loading) return;
    trackPlanSelected('VOY_FREE', isAnnual);
    setError(null);
    setNotice(null);
    // Every early return below sets a message; the dialog path is visible on its own.
    scrollToStatusRef.current = true;

    if (!user) {
      setError('Please sign in to change your plan');
      return;
    }

    let current: SubscriptionSnapshot = {
      plan: currentPlan,
      subscriptionId: currentSubscriptionId,
      cancelAtPeriodEnd,
      periodEndLabel,
    };
    if (!current.plan) {
      // A null plan isn't proof of Free: the initial load may still be in flight, or may have
      // failed (getCurrentSubscription returns null on errors). Re-check before saying so.
      setLoading(true);
      try {
        current = await loadCurrentSubscription();
      } finally {
        setLoading(false);
      }
    }

    if (!current.plan) {
      // Without the prices a paid plan can't be recognized, so no plan is not proof of Free.
      // (Cached after the first load, so no extra request.)
      const pricesLoaded = await loadSubscriptionPrices().then(() => true, () => false);
      if (!pricesLoaded) {
        setError(PRICES_UNAVAILABLE);
        return;
      }
      setNotice("You're already on the Free plan.");
      return;
    }
    if (current.cancelAtPeriodEnd) {
      setNotice(
        `Your ${STRIPE_PRODUCTS[current.plan].name} plan is already set to switch to Free${current.periodEndLabel ? ` on ${current.periodEndLabel}` : ' at the end of your billing period'}.`
      );
      return;
    }
    if (!current.subscriptionId) {
      // e.g. past_due: the plan is shown but not manageable here (see VOY-525 note above).
      setError('Your subscription cannot be changed right now. Please contact support.');
      return;
    }
    scrollToStatusRef.current = false;
    setShowDowngradeDialog(true);
  };

  const handleConfirmDowngrade = async () => {
    if (!currentSubscriptionId) return;
    setLoading(true);
    setError(null);
    setNotice(null);
    scrollToStatusRef.current = true;
    try {
      const updated = await cancelSubscriptionAtPeriodEnd(currentSubscriptionId);
      setCancelAtPeriodEnd(true);
      setPeriodEndLabel(formatPeriodEnd(updated?.current_period_end) ?? periodEndLabel);
      setShowDowngradeDialog(false);
    } catch (err) {
      console.error('[handleConfirmDowngrade] Failed to cancel subscription:', err);
      setError(err instanceof Error && err.message ? err.message : 'Failed to downgrade your plan. Please try again.');
      setShowDowngradeDialog(false);
    } finally {
      setLoading(false);
    }
  };

  /** VOY-608: undo a pending downgrade from the banner — same plan, no new charge. */
  const handleKeepPlan = async () => {
    if (!currentSubscriptionId || !currentPlan || loading) return;
    setLoading(true);
    setError(null);
    setNotice(null);
    scrollToStatusRef.current = true;
    try {
      const updated = await reactivateSubscription(currentSubscriptionId);
      const renewsOn = formatPeriodEnd(updated?.current_period_end) ?? periodEndLabel;
      setCancelAtPeriodEnd(false);
      setPeriodEndLabel(renewsOn);
      setNotice(
        `You're keeping your ${STRIPE_PRODUCTS[currentPlan].name} plan. It will renew${renewsOn ? ` on ${renewsOn}` : ' at the end of your billing period'}.`
      );
    } catch (err) {
      console.error('[handleKeepPlan] Failed to reactivate subscription:', err);
      setError(err instanceof Error && err.message ? err.message : 'Failed to keep your plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (priceId: string) => {
    // Get plan name from priceId for tracking (before any early returns)
    const planInfo = getPlanFromPriceId(priceId, prices);
    const planName = planInfo?.plan || 'unknown';
    
    // Track plan selection (even for logged-out users)
    trackPlanSelected(planName, isAnnual);
    
    if (!user) {
      setError('Please sign in to subscribe');
      return;
    }
    
    setLoading(true);
    setError(null);
    setNotice(null);

    try {

      // Always check for active subscription first (in case state is stale)
      // First try from database view
      const currentSub = await getCurrentSubscription();
      
      // Also check directly from Stripe API to be sure
      let activeSubscriptionId = null;
      let stripeApiError = null;
      
      try {
        const activeSubsResponse = await apiFetch('/api/subscriptions/active', {
          method: 'GET',
        });
        
        if (activeSubsResponse.ok) {
          const activeSubsData = await activeSubsResponse.json();
          
          if (activeSubsData.subscriptions && activeSubsData.subscriptions.length > 0) {
            // Use the most recent active subscription
            const sortedSubs = activeSubsData.subscriptions.sort((a: { created: number }, b: { created: number }) => b.created - a.created);
            activeSubscriptionId = sortedSubs[0].id;
          }
        } else {
          const errorData = await activeSubsResponse.json().catch(() => ({ error: 'Unknown error' }));
          stripeApiError = errorData.error || 'Failed to get active subscriptions';
          console.warn('[handleSubscribe] ⚠️ Stripe API returned error:', errorData);
        }
      } catch (apiError) {
        stripeApiError = (apiError as Error).message || 'Failed to get active subscriptions';
        console.warn('[handleSubscribe] ⚠️ Failed to get active subscriptions from API:', apiError);
      }
      
      // If Stripe API didn't return a subscription, use database view as fallback
      if (!activeSubscriptionId) {
        if (currentSub?.subscription_id && 
            (currentSub.status === 'active' || currentSub.status === 'trialing')) {
          activeSubscriptionId = currentSub.subscription_id;
        }
      }
      
      // If we still don't have a subscription ID but there was a Stripe API error,
      // log a warning but don't block - use DB view as fallback
      if (!activeSubscriptionId && stripeApiError) {
        console.warn('[handleSubscribe] ⚠️ Could not verify active subscriptions via API, using DB view as fallback:', stripeApiError);
        // Continue with DB view - don't block the user
      }

      // If user has an active subscription, update it instead of creating a new one
      if (activeSubscriptionId) {
        // Check if the price is actually different
        if (currentSub?.price_id === priceId) {
          setError('You are already subscribed to this plan');
          setLoading(false);
          return;
        }
        
        try {
          const updateUrl = `/api/subscriptions/${activeSubscriptionId}/update`;
          
          const response = await apiFetch(updateUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ newPriceId: priceId }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error('[handleSubscribe] ❌ Update failed:', errorData);
            throw new Error(errorData.error || 'Failed to update subscription');
          }

          // Wait a moment for database to update
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Reload subscription data to reflect the update
          await loadCurrentSubscription();
          
          setError(null);
          // Redirect to profile to see updated subscription
          window.location.href = '/profile';
          return; // IMPORTANT: Don't continue to checkout
        } catch (caughtUpdateError) {
          const updateError = caughtUpdateError as Error;
          console.error('[handleSubscribe] ❌ Update error:', updateError);
          console.warn('[handleSubscribe] ⚠️ Update endpoint failed, falling back to checkout session');
          
          // Fall back to creating a checkout session if update fails
          // This handles cases where the update endpoint doesn't exist or returns an error
          try {
            // Get plan name from priceId for tracking (reuse from earlier in function)
            const fallbackPlanInfo = getPlanFromPriceId(priceId, prices);
            const fallbackPlanName = fallbackPlanInfo?.plan || 'unknown';
            
            // Track checkout started for fallback path
            trackCheckoutStarted(fallbackPlanName, isAnnual);
            
            // Generate unique checkout ID for this checkout session
            const checkoutId = `${priceId}_${Date.now()}`;
            
            // Store checkout info in sessionStorage for success page tracking
            sessionStorage.setItem('last_subscription_price_id', priceId);
            sessionStorage.setItem('last_checkout_id', checkoutId);
            
            const checkoutUrl = await createCheckoutSession(priceId, 'subscription');
            window.location.href = checkoutUrl;
            return;
          } catch (checkoutError) {
            console.error('[handleSubscribe] ❌ Checkout fallback also failed:', checkoutError);
            setError(`Unable to update subscription. Please try again or contact support. Error: ${updateError.message}`);
            setLoading(false);
            return;
          }
        }
        } else {
        // Track checkout started
        trackCheckoutStarted(planName, isAnnual);
        
        // Generate unique checkout ID for this checkout session
        // This allows tracking multiple checkouts of the same plan in the same session
        const checkoutId = `${priceId}_${Date.now()}`;
        
        // Store checkout info in sessionStorage for success page tracking
        sessionStorage.setItem('last_subscription_price_id', priceId);
        sessionStorage.setItem('last_checkout_id', checkoutId);
        
        // No active subscription, create a new one via checkout
        const checkoutUrl = await createCheckoutSession(priceId, 'subscription');
        
        window.location.href = checkoutUrl;
      }
    } catch (error) {
      console.error('[handleSubscribe] ❌ Subscription error:', error);
      setError((error as Error).message || 'Failed to process subscription');
      setLoading(false);
    }
  };

  const proPriceId = getPriceId('VOY_PRO', isAnnual, prices);
  const shownError = error ?? (pricesStatus === 'error' ? PRICES_UNAVAILABLE : null);

  return (
    <div className="min-h-screen bg-transparent">
      <div className="container mx-auto py-6 md:py-12 px-4">
        {/* Hero Section - Following Notion/Slack design */}
        <div className="text-center mb-8 md:mb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-3 md:mb-4 leading-tight text-white drop-shadow-lg">
            Travel Smarter, <span className="text-[#5B6FED]">Save More</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-6 md:mb-8 drop-shadow-md px-2">
            Unlock special subscriber pricing on all bookings.
          </p>
          
          {/* Annual/Monthly Toggle - Following Notion/Slack pattern */}
          <div className="flex items-center justify-center gap-3 md:gap-4 mb-6 md:mb-8 flex-wrap">
            <span className={cn('text-sm md:text-base transition-colors text-white/90', !isAnnual ? 'font-semibold' : 'opacity-60')}>
              Monthly
            </span>
            <Switch
              checked={isAnnual}
              onCheckedChange={(checked) => {
                setIsAnnual(checked);
                trackSubscriptionToggle(checked);
              }}
              className="data-[state=checked]:bg-[#5B6FED] w-11 h-6 [&>span[data-state=checked]]:translate-x-[24px]"
            />
            <span className={cn('text-sm md:text-base transition-colors text-white/90', isAnnual ? 'font-semibold' : 'opacity-60')}>
              Annual
            </span>
            {isAnnual && (
              <span className="bg-green-500/90 backdrop-blur-sm text-white px-2.5 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium ml-1 md:ml-2 shadow-lg">
                Save 20%
              </span>
            )}
          </div>
        </div>
        
        <div ref={statusRef}>
        {shownError && (
          <div className="max-w-md mx-auto mb-8 p-4 bg-destructive/10 text-destructive rounded-full text-center">
            {shownError}
          </div>
        )}

        {/* VOY-607: pending downgrade to Free */}
        {currentPlan && cancelAtPeriodEnd && (
          <div
            data-testid="pending-downgrade-banner"
            className="max-w-xl mx-auto mb-8 p-4 bg-amber-500/15 text-amber-100 border border-amber-400/30 rounded-2xl text-center"
          >
            {/* Live region holds only the message; the button stays outside it. */}
            <p role="status">
              Your {STRIPE_PRODUCTS[currentPlan].name} plan is canceled and will switch to Free
              {periodEndLabel ? ` on ${periodEndLabel}` : ' at the end of your billing period'}. You keep your
              benefits until then.
            </p>
            {currentSubscriptionId && (
              <div className="mt-3">
                <Button
                  type="button"
                  onClick={() => void handleKeepPlan()}
                  disabled={loading}
                  className="rounded-full bg-white text-gray-900 hover:bg-white/90"
                >
                  {loading ? 'Processing...' : 'Keep my plan'}
                </Button>
              </div>
            )}
          </div>
        )}

        {notice && (
          <div role="status" className="max-w-md mx-auto mb-8 p-4 bg-white/10 text-white rounded-full text-center">
            {notice}
          </div>
        )}
        </div>
        
        {/* Pricing Cards Grid - Following Notion/Slack layout */}
        {/* Filter out VOY_FREE as it's not purchasable, show 3 paid tiers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-7xl mx-auto mb-12 md:mb-16">
          {Object.entries(STRIPE_PRODUCTS)
            .filter(([key]) => key !== 'VOY_FREE') // Exclude free tier from purchase options
            .map(([key]) => {
              const plan = key as StripePlan;
              // Family (VOY_PRO) is recommended for most value
              const isRecommended = plan === 'VOY_PRO';
              // Current plan only if both plan and billing interval match
              const isCurrentPlan = currentPlan === plan && currentSubscriptionIsAnnual === isAnnual;
              
              return (
                <PricingCard
                  key={plan}
                  plan={plan}
                  isCurrentPlan={isCurrentPlan}
                  isRecommended={isRecommended}
                  isAnnual={isAnnual}
                  priceId={getPriceId(plan, isAnnual, prices)}
                  onSubscribe={handleSubscribe}
                  loading={loading}
                />
              );
            })}
        </div>

        {/* Features Comparison Table */}
        <FeaturesComparison currentPlan={currentPlan} />
      </div>

      {/* All plans, including Free (VOY-607 downgrade entry point) */}
      <SavingsCalculator
        isAnnual={isAnnual}
        onSubscribe={(plan) => {
          if (plan === 'VOY_FREE') {
            void handleSelectFree();
            return;
          }
          const priceId = getPriceId(plan, isAnnual, prices);
          if (!priceId) {
            setError(PRICES_UNAVAILABLE);
            return;
          }
          handleSubscribe(priceId);
        }}
      />

      <AlertDialog
        open={showDowngradeDialog}
        onOpenChange={(open) => {
          if (!loading) setShowDowngradeDialog(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch to the Free plan?</AlertDialogTitle>
            <AlertDialogDescription>
              Your {currentPlan ? STRIPE_PRODUCTS[currentPlan].name : 'current'} subscription will be canceled
              {periodEndLabel ? ` on ${periodEndLabel}` : ' at the end of your billing period'}. You keep
              subscriber pricing until then and won't be charged again. You can resubscribe anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDowngradeDialog(false)} disabled={loading}>
              Keep my plan
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDowngrade} disabled={loading}>
              {loading ? 'Processing...' : 'Switch to Free'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* FAQ Section */}
      <FAQSection />

      {/* Mobile Sticky CTA - Following Notion/Slack pattern */}
      <div 
        className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-white/20 shadow-2xl z-50 p-4"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <Button
          onClick={() => {
            // Pro plan price ID (recommended plan)
            if (proPriceId) {
              handleSubscribe(proPriceId);
            }
          }}
          disabled={loading || !proPriceId || (currentPlan === 'VOY_PRO' && currentSubscriptionIsAnnual === isAnnual)}
          className={cn(
            'w-full py-4 text-base font-semibold rounded-full transition-all min-h-[52px]',
            'bg-gradient-to-r from-[#5B6FED] to-[#4C5FD8] hover:from-[#4C5FD8] hover:to-[#3D4EC7]',
            'text-white shadow-lg',
            (loading || !proPriceId || (currentPlan === 'VOY_PRO' && currentSubscriptionIsAnnual === isAnnual)) && 'opacity-50 cursor-not-allowed'
          )}
        >
          {loading ? (
            'Processing...'
          ) : currentPlan === 'VOY_PRO' && currentSubscriptionIsAnnual === isAnnual ? (
            'Current Plan'
          ) : (
            <>
              Start Saving Now
              {isAnnual && (
                <span className="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded-full">
                  Save 20%
                </span>
              )}
            </>
          )}
        </Button>
      </div>

      {/* Add bottom padding on mobile to prevent content from being hidden behind sticky CTA */}
      <div className="md:hidden h-32" />
    </div>
  );
}
